import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnchorLink } from 'components/anchorLink';
import { Icon, IconsEnum, toIconsEnum } from 'components/Icons';
import { SkeletonArea } from 'components/skeletonArea';
import { isMatch } from 'date-fns';
import { selectDisciplinaBySlug } from 'features/live/liveNav/selectors';
import sportNavAccordionContentStyles from 'features/sport/components/sportNav/sportNavAccordion/components/sportNavAccordionContent/SportNavAccordionContent.module.scss';
import sportNavAccordionTriggerStyles from 'features/sport/components/sportNav/sportNavAccordion/components/sportNavAccordionTrigger/SportNavAccordionTrigger.module.scss';
import sportNavAccordionStyles from 'features/sport/components/sportNav/sportNavAccordion/SportNavAccordion.module.scss';
import sportNavAccordionListStyles from 'features/sport/components/sportNav/sportNavAccordionList/SportNavAccordionList.module.scss';
import { useVirtualDom } from 'hooks';
import { useTranslation } from 'hooks/useLingUI';
import { useSelector } from 'react-redux';
import { isClientSide } from 'utility/constant';
import { isTruthy, toSportUrlCase } from 'utility/functions';
import { seoItemHierarchy } from 'components/staticMap';
import useWindowSize from 'hooks/useWindowSize';

export interface StaticMenuProps {
  item?: seoItemHierarchy | undefined | null;
  onMountedDestroy?: boolean;
}

export const StaticMenu = ({ item, onMountedDestroy = true }: StaticMenuProps) => {
  const refNav = useRef<HTMLDivElement>(null);
  const [idOpen, setOpen] = useState('');
  const { isMobile } = useWindowSize();

  const allValidIds: Array<string> = useMemo(
    () => (item?.children ?? []).filter((x) => !isMatch(`${x.self?.title}`, '^info$')).map((x) => `${x.self?.uid}`),
    []
  );

  const { keys, heights, wrapperRef } = useVirtualDom<HTMLUListElement>({ uids: allValidIds, counter: 20 });

  useEffect(() => {
    if (onMountedDestroy && isClientSide && refNav.current instanceof HTMLDivElement) {
      refNav.current.innerHTML = '';
    }
  }, [onMountedDestroy]);

  const toggle = (nextValue: string): void => {
    setOpen((st) => {
      if (st === nextValue) return '';
      return nextValue;
    });
  };

  const RootItem = (item: seoItemHierarchy, clasName: string, isOpen: boolean, onOpenToggle: Function) => {
    const { t } = useTranslation();
    const { self, children } = item;
    const { uid, loc, title: def } = self ?? {};

    const title = `${loc ?? def}`.split('/').pop();

    const { id, descrizione, descrizioneTrKey } = useSelector((st: any) => selectDisciplinaBySlug(st, title ?? ''));

    if (title) {
      const icon = toIconsEnum(`DISCIPLINA_${id}`) ?? IconsEnum.DISCIPLINA_FALLBACK;
      const txt = t(descrizioneTrKey, descrizione ?? title);

      const menunuItems = Array.isArray(children) ? [...children] : [];
      if (loc) {
        menunuItems.unshift(item);
      }
      return (
        <li
          data-uid={uid}
          key={`${title}-${loc}`}
          className={clasName}
          data-state={isOpen ? 'open' : 'closed'}
          data-orientation="vertical"
        >
          {isMobile || keys.includes(`${uid}`) ? (
            <React.Fragment>
              <h3 data-orientation="vertical" data-state={isOpen ? 'open' : 'closed'} onClick={() => onOpenToggle(uid)}>
                <button
                  type="button"
                  data-state={isOpen ? 'open' : 'closed'}
                  aria-expanded={isOpen}
                  data-orientation="vertical"
                  className={sportNavAccordionTriggerStyles.container}
                >
                  <span className={sportNavAccordionTriggerStyles.iconContainer}>
                    <Icon iconId={icon} className={sportNavAccordionTriggerStyles.iconCountry} />
                  </span>
                  <p className={sportNavAccordionTriggerStyles.text}>{txt}</p>
                  {isTruthy(menunuItems?.length) && (
                    <Icon iconId={IconsEnum.CHEVRON_DOWN} className={sportNavAccordionTriggerStyles.icon} />
                  )}
                </button>
              </h3>
              {isTruthy(menunuItems?.length) && (
                <div
                  role="region"
                  style={{ display: isOpen ? undefined : 'none' }}
                  className={sportNavAccordionContentStyles.container}
                  data-state={isOpen ? 'open' : 'closed'}
                  data-orientation="vertical"
                >
                  <ul className={sportNavAccordionContentStyles.list}>
                    {menunuItems?.map(({ self, children }, idx) => {
                      const { loc, title: def } = self ?? {};
                      const title = `${loc ?? def}`.split('/').pop();
                      return (
                        <li
                          key={`${idx}-${loc}`}
                          className={sportNavAccordionContentStyles.listItem}
                          suppressHydrationWarning
                        >
                          <AnchorLink
                            href={toSportUrlCase(`${loc}`)}
                            target={'_self'}
                            className={sportNavAccordionContentStyles.link}
                          >
                            {isTruthy(children?.length) ? <b>{title}</b> : <span>{title}</span>}
                          </AnchorLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </React.Fragment>
          ) : (
            <SkeletonArea
              height="4"
              className={sportNavAccordionListStyles.loader}
              style={{ height: heights[uid!], margin: 0 }}
            />
          )}
        </li>
      );
    }
    return null;
  };

  if (isTruthy(item?.children?.length))
    return (
      <div ref={refNav} className={sportNavAccordionListStyles.container}>
        <nav>
          <ul data-orientation={'vertical'} ref={wrapperRef}>
            {item?.children
              ?.filter((x) => !isMatch(`${x.self?.title}`, '^info$'))
              ?.map((x) => RootItem(x, sportNavAccordionStyles.item, idOpen === x.self?.uid, toggle))}
          </ul>
        </nav>
      </div>
    );

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <React.Fragment />;
};